import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SimpleSpinner from '../simple_spinner/simple_spinner';
import { subscribeToNewsletter } from '../../../services/newsletter/newsletter.api.service';
import './email_subscribe_form.scss';

const EmailSubscribeForm = ({ newsletter, titlePrefix, showInfoOnSuccess, buttonClass = '' }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewsletterInfo, setShowNewsletterInfo] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  /**
   * Function triggered when user submits an email to be subscribed.
   * Hides any kind of error message, and does a request
   * to subscribeToNewsletter with:
   *   - newsletter id
   *   - email from the input
   *   - referrer (url + newsletter permalink)
   *
   * Depending on the response, shows/hides info and error messages
   *
   * @param {Event} event - Event triggered by browser
   * @returns {undefined}
   */
  const handleSubscription = (event) => {
    event.preventDefault();

    setShowErrorMessage(false);
    setIsLoading(true);

    const referrer = `${window.location.href}#${newsletter.permalink}`;

    subscribeToNewsletter(newsletter, { email, referrer })
      .then(() => {
        setShowSuccessMessage(true);
        setShowNewsletterInfo(showInfoOnSuccess);
      })
      .catch(() => {
        setShowErrorMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderIcon = newsletter.icon && (
    <i data-testid='newsletter-icon' className={`icon fal icon-${newsletter.icon}`}></i>
  );

  const renderTitle = newsletter.name && (
    <h3 className='fs-5 newsletter-title'>
      {titlePrefix}
      {newsletter.name}
    </h3>
  );

  const renderDescription = newsletter.description && (
    <p data-testid='newsletter-description' className='newsletter-description text-gray'>
      {newsletter.description}
    </p>
  );

  const renderForm = (
    <form
      className='subscription-email-form'
      role='form'
      aria-label='form'
      onSubmit={handleSubscription}
    >
      <input
        id='email'
        name='email'
        autoComplete='email'
        type='email'
        placeholder='Email'
        className='form-control'
        required
        aria-label={email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      ></input>
      <button
        id={`subscribe-button-${newsletter.permalink}`}
        className={`${buttonClass} btn btn-outline-primary strong`}
        disabled={isLoading}
        type='submit'
      >
        <span className='text'>Subscribe</span>
      </button>
      <div className='spinner-container'>
        <SimpleSpinner display={isLoading} opacity={0.85} />
      </div>
    </form>
  );

  const renderSuccessMessage = (
    <p className='success-message text-gray' data-testid='success-message'>
      <span>Thanks for subscribing to the newsletter!</span>
    </p>
  );

  const renderErrorMessage = (
    <p className='error-message small text-red mt-2' data-testid='error-message'>
      <i className='fal icon-exclamation-circle me-1'></i>
      <span>Something went wrong. Please try again.</span>
    </p>
  );

  return (
    <div data-testid='email-subscribe-form' className='email-subscribe-form'>
      {renderIcon}
      {showNewsletterInfo && renderTitle}
      {showNewsletterInfo && renderDescription}
      {showSuccessMessage ? renderSuccessMessage : renderForm}
      {showErrorMessage && renderErrorMessage}
    </div>
  );
};

EmailSubscribeForm.propTypes = {
  newsletter: PropTypes.shape({
    id: PropTypes.number.isRequired,
    icon: PropTypes.string,
    name: PropTypes.string,
    permalink: PropTypes.string,
    description: PropTypes.string,
    accent_color: PropTypes.string,
  }),
  titlePrefix: PropTypes.string,
  showInfoOnSuccess: PropTypes.bool,
  buttonClass: PropTypes.string,
};

export default EmailSubscribeForm;
